import type { ComponentProps } from 'react'
import React, { memo, useMemo, useContext, useCallback } from 'react'
import styled from 'styled-components'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { pushWithCruiseFilters } from 'actions/CruiseActions'
import useQueryParams from 'hooks/useQueryParams'
import Carousel from 'LuxKit/Carousel/Carousel'
import CruiseDepartureSelect from 'components/Cruises/SearchPage/Filters/Inputs/CruiseDepartureSelect/CruiseDepartureSelect'
import CruiseLocationFilter from 'components/Cruises/SearchPage/Filters/Inputs/CruiseLocationFilter/CruiseLocationFilter'
import CruiseWhenSelect from 'components/Cruises/SearchPage/Filters/Inputs/CruiseWhenSelect/CruiseWhenSelect'
import { getCruiseSearchFiltersBar } from 'lib/cruises/cruiseUtils'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import CruiseHeaderFilterChip from 'components/Cruises/SearchPage/Filters/CruiseHeaderFilterChip'

const CruiseSearchFiltersBar = styled.div`
  position: relative;
`

type InheritedCruiseHeaderFilterChipProps = Pick<ComponentProps<typeof CruiseHeaderFilterChip>, 'onSortChange'>

interface Props extends InheritedCruiseHeaderFilterChipProps {
  filters: App.OfferListFilters
}

function CruiseDealFilterChips({ filters, onSortChange }: Props) {
  const dispatch = useAppDispatch()
  const globalSearch = useContext(GlobalSearchStateContext)
  const pathname = useAppSelector(state => state.router.location.pathname)
  const regionCode = useAppSelector(state => state.geo.currentRegionCode)
  const queryParams = useQueryParams()

  const onChange = useCallback((values: App.CruiseGlobalFilters) => {
    const { pageName, filters } = getCruiseSearchFiltersBar(values, globalSearch, pathname, regionCode, false)
    dispatch(pushWithCruiseFilters(pageName, filters, queryParams))
  }, [globalSearch, dispatch, pathname, regionCode, queryParams])

  const initialFilters = useMemo(() => ({
    cruiseLines: filters.cruiseLines ?? [],
    rateCodes: filters.rateCodes ?? [],
  }), [filters])

  return <CruiseSearchFiltersBar>
    <Carousel gap={8}>
      <CSSBreakpoint only="mobile">
        <CruiseHeaderFilterChip filters={filters} hideChips onSortChange={onSortChange} />
      </CSSBreakpoint>

      <CruiseLocationFilter
        onChange={onChange}
        isChipMode
        dropdownSize="S"
        hideImages
        hideTrendOptions
        initialFilters={initialFilters}
        shouldIgnoreFlashOffers
      />

      <CruiseDepartureSelect
        onChange={onChange}
        isChipMode
        shouldDisableRegionTabs
        dropdownSize="S"
        initialFilters={initialFilters}
        shouldIgnoreFlashOffers
      />

      <CruiseWhenSelect
        onChange={onChange}
        isChipMode
        calendarSize="S"
        dropdownSize="M"
        initialFilters={initialFilters}
        shouldIgnoreFlashOffers
      />
    </Carousel>
  </CruiseSearchFiltersBar>
}

export default memo(CruiseDealFilterChips)
